/**
 * FormList - Form listing page.
 *
 * Displays all forms in a table with search, create, edit, and delete actions.
 */

import { PageHeader } from '@/shared/components';
import { showErrorToast, showSuccessToast } from '@/shared/notifications/toast-utils';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui';
import { FileText, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useDeleteForm, useFormList } from '../../hooks/useFormBuilder';

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '-';
  }
}

export default function FormList() {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useFormList();
  const deleteMutation = useDeleteForm();

  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const forms = data?.values ?? [];

  const filtered = search.trim()
    ? forms.filter(
        (f) =>
          f.name?.toLowerCase().includes(search.toLowerCase()) ||
          f.description?.toLowerCase().includes(search.toLowerCase()),
      )
    : forms;

  const handleDelete = async () => {
    if (deleteId === null) return;
    try {
      await deleteMutation.mutateAsync(deleteId);
      showSuccessToast('Form deleted successfully.');
    } catch {
      showErrorToast('Unable to delete form. Please try again.');
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <PageHeader
        title="Form Builder"
        subtitle="Create and manage forms for data capture"
        icon={<FileText className="w-8 h-8" style={{ color: '#E84A2B' }} />}
        actions={
          <Button
            onClick={() => navigate('/settings/form-builder/new')}
            className="bg-[#E84A2B] hover:bg-[#d43d20] text-white"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            New Form
          </Button>
        }
      />

      <div className="px-6 pb-6 flex-1">
        {/* Search bar */}
        <div className="mb-4 relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search forms..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="text-center py-12">
            <p className="text-sm text-muted-foreground">Loading forms...</p>
          </div>
        )}

        {/* Error state */}
        {isError && (
          <div className="text-center py-12">
            <p className="text-sm text-red-500">Unable to load forms. Please try again.</p>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !isError && forms.length === 0 && (
          <div className="text-center py-16 border rounded-lg bg-muted/30">
            <FileText className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
            <h3 className="text-sm font-semibold text-foreground">No forms yet</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Create your first form to get started.
            </p>
            <Button
              onClick={() => navigate('/settings/form-builder/new')}
              className="mt-4 bg-[#E84A2B] hover:bg-[#d43d20] text-white"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              New Form
            </Button>
          </div>
        )}

        {/* Table */}
        {!isLoading && !isError && filtered.length > 0 && (
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40%]">Name</TableHead>
                  <TableHead className="w-[25%]">Description</TableHead>
                  <TableHead className="w-[15%]">Created</TableHead>
                  <TableHead className="w-[10%]">Type</TableHead>
                  <TableHead className="w-[10%] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((formItem) => (
                  <TableRow
                    key={formItem.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/settings/form-builder/${formItem.id}`)}
                  >
                    <TableCell className="font-medium text-foreground">
                      <div className="flex items-center gap-2">
                        {formItem.name}
                        {formItem.is_system && (
                          <Badge variant="secondary" className="text-[10px]">
                            System
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground truncate max-w-[200px]">
                      {formItem.description || '-'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(formItem.created_time)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formItem.form_type_name || '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/settings/form-builder/${formItem.id}`);
                          }}
                          className="p-1.5 rounded hover:bg-muted transition-colors"
                          title="Edit form"
                        >
                          <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteId(formItem.id);
                          }}
                          className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-950 transition-colors"
                          title="Delete form"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-muted-foreground hover:text-red-500" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* No search results */}
        {!isLoading && !isError && forms.length > 0 && filtered.length === 0 && (
          <div className="text-center py-12 border rounded-lg bg-muted/30">
            <p className="text-sm text-muted-foreground">No forms match your search.</p>
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      <Dialog open={deleteId !== null} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete form</DialogTitle>
            <DialogDescription>
              This action cannot be undone. The form and all its sections and fields will be
              permanently deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
